import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useUser } from '../../auth'
import AuthLayout from '../../components/auth/AuthLayout'
import NewPasswordForm from '../../components/auth/NewPasswordForm'
import { getById, update_new_password } from '../../server/server'
import { usePageTitle } from '../../utils/util'
import { useAppContext } from '../../context/AppContext'
import { checkIfTeacherHasPrimaryClasses, checkIfTeacherHasRegularClasses } from '../../server/usersData'
import { isItAccess } from '../../constants/Roles'
import i18n from 'i18next'

const SetPasswordScreen = () => {
    usePageTitle(`Upstrive Education - Set Password`)
    const history = useHistory()
    const location = useLocation()
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const { setUser } = useUser()
    const { appState, setAppState } = useAppContext()

    useEffect(() => {
        // Get token and email from URL params
        const token = localStorage.getItem('token')

        if (!token) {
            history.push('/login')
        }
    }, [location, history])

    const onNewPasswordSubmit = async (password) => {
        setIsLoading(true)
        setErrorMessage('')
        const user = JSON.parse(localStorage.getItem('user'))

        try {
            const data = {
                email: user?.email,
                password: password,
                token: localStorage.getItem('token'),
            }
            const resp = await update_new_password(data)

            if (resp.status === 200) {
                const user = resp.data
                const data = resp.data

                if (data.role === 0) {
                    const token = data['token']
                    window.open(`https://student.upstrivesystem.com?token=${token}&locale=${data.locale ?? 'en'}`, '_self')
                    setErrorMessage("You will be redirected to student's page...")
                    setIsLoading(false)
                    return
                }

                if (isItAccess(user)) {
                    // IT Access only manages users and has no classes; skip the
                    // primary/regular class checks and land on User Management,
                    // mirroring the same early-exit used in LoginScreen.
                    setUser(user)
                    localStorage.setItem('uiMode', 'regular')
                    setAppState({ ...appState, primary: false })
                    history.push('/management')
                    return
                }

                const hasPrimaryClasses = await checkIfTeacherHasPrimaryClasses()
                if (hasPrimaryClasses) {
                    const hasRegularClasses = await checkIfTeacherHasRegularClasses()
                    setUser({ ...user, hasPrimaryClasses, hasRegularClasses, hasOnlyPrimaryStudents: !hasRegularClasses })
                    const primary = !hasRegularClasses
                    localStorage.setItem('uiMode', primary ? 'primary' : 'regular')
                    setAppState({ ...appState, primary })
                } else {
                    setUser(user)
                    localStorage.setItem('uiMode', 'regular')
                    setAppState({ ...appState, primary: false })
                }

                await loadInstitution(data)
                history.push('/dashboard')
            } else {
                // Non-200 (e.g. 403 permission denied). handelError in server.js
                // returns rather than throws, so this branch is the one that
                // surfaces server errors. Show the server's message when present
                // so the user sees why the request failed (e.g. the it_access
                // role is blocked from update_new_password until allowlisted).
                const serverMessage =
                    resp?.data?.message ||
                    resp?.data ||
                    'Could not set password. Please try again or contact support.'
                setErrorMessage(typeof serverMessage === 'string' ? serverMessage : 'Could not set password. Please try again or contact support.')
            }
        } catch (e) {
            console.log(e)
            setErrorMessage('Could not set password. Please try again or contact support.')
        }

        setIsLoading(false)
    }

    const loadInstitution = async (user) => {
        const resp = await getById({ collection: 'institutions', id: user.institution_id })
        if (resp.status === 200) {
            if (!!resp?.data?.locale) {
                i18n.changeLanguage(resp?.data?.locale)
                localStorage.setItem('locale', resp?.data?.locale)
            }
        }
    }

    const handleBack = () => {
        history.push('/login')
    }

    return (
        <AuthLayout>
            <NewPasswordForm onSubmit={onNewPasswordSubmit} onBack={handleBack} isLoading={isLoading} errorMessage={errorMessage} />
        </AuthLayout>
    )
}

export default SetPasswordScreen
